import { ft } from '../core/units.js';
import { STAFF_BY_ID } from '../data/staff.js';

/**
 * Errands.
 *
 * Two things produce one: a trade ("do this first and I will give you the
 * thing") and a favour being called in ("you owe me, and I want it now"). They
 * are the same object because they are the same experience — a second deadline
 * dropped into a shift that already has one, somewhere else on the property.
 *
 * Every errand is *go there*. Not fetch, not deliver, not escort: walk to a
 * named spot before a time and press E. That is deliberately the only shape.
 * The property is two hundred and fifty feet long and the cost of an errand is
 * the walk, which is a cost the player already understands and which cannot
 * deadlock — a fetch errand for an item the storeroom has run out of would be
 * an errand nobody can finish, and the shortfall that caused it is exactly the
 * situation an errand gets handed out in.
 *
 * (A fetch variant is the obvious next one. It needs a guarantee that the item
 * is obtainable, which is a whole argument with `stores`, and it is not worth
 * having until errands are more than a minute old as a mechanic.)
 */

/** How close you have to be to the spot. Generous: it is a place, not a button. */
export const ERRAND_REACH = ft(9);

/** Where a person stands, as an errand destination. */
export function spotFor(staffId) {
  const person = STAFF_BY_ID[staffId];
  if (!person) return null;
  const [x, z] = person.route[0];
  return { staffId, label: person.name, department: person.department, x, z };
}

export function createTasks({ max = 3 } = {}) {
  let list = [];
  const listeners = new Set();

  function emit(event, payload) {
    for (const listener of listeners) listener(event, payload);
  }

  return {
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    get list() {
      return list;
    },
    get count() {
      return list.length;
    },
    byId: (id) => list.find((task) => task.id === id) ?? null,

    /** Whether there is room for another. Three at once is already a bad shift. */
    get full() {
      return list.length >= max;
    },

    add(task) {
      if (list.length >= max) return { ok: false, reason: 'too-many' };
      list = [...list, { ...task, expired: false }];
      emit('added', task);
      return { ok: true, task };
    },

    /** Fires `expired` once per task, whether or not the player is nearby. */
    update(clock) {
      const now = clock.day * 1440 + clock.minutes;
      for (const task of list) {
        if (task.expired || now < task.dueAbsolute) continue;
        task.expired = true;
        emit('expired', task);
      }
    },

    /** The errand you are standing on, if any. */
    nearest(position) {
      let best = null;
      for (const task of list) {
        if (task.expired) continue;
        const distance = Math.hypot(position.x - ft(task.target.x), position.z - ft(task.target.z));
        if (distance > ERRAND_REACH) continue;
        if (!best || distance < best.distance) best = { task, distance };
      }
      return best;
    },

    complete(id) {
      const task = list.find((entry) => entry.id === id);
      if (!task) return null;
      list = list.filter((entry) => entry.id !== id);
      emit('completed', task);
      return task;
    },

    /** Removes without completing. Refusing a favour, or a run resetting. */
    drop(id) {
      const task = list.find((entry) => entry.id === id);
      if (!task) return null;
      list = list.filter((entry) => entry.id !== id);
      emit('dropped', task);
      return task;
    },

    clear() {
      list = [];
    },

    toJSON: () => ({ list }),
    load(state) {
      list = [...(state?.list ?? [])];
    },
  };
}

/** Minutes from now to an errand's deadline, as an absolute day-and-minute. */
export function dueIn(clock, minutes) {
  return clock.day * 1440 + clock.minutes + minutes;
}
